/**
 * TOSS Web Application — Client portal: "no connection" screen
 *
 * What the installed portal shows when it starts with no network. It exists because the
 * alternative was worse than useless: `initialize()` used to treat a transport failure the same
 * as a dead session, wipe the tokens and hand the customer a login form — which offline cannot
 * submit either. Opening the app in a lift meant being signed out for real.
 *
 * Deliberately NOT the shared `PageErrorState`: that one lives in the internal app's language and
 * assumes a page chrome around it. This screen is the whole viewport, because at this point the
 * portal has no chrome yet — the session it would be drawn for is not confirmed.
 *
 * The way out is either button or automatic: `AuthInitializer` retries on `window.online`.
 */

import { WifiOff } from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '@/store';

export function ClientOfflineScreen(): JSX.Element {
  const initialize = useAuthStore((s) => s.initialize);
  const [isRetrying, setRetrying] = useState(false);

  const retry = async (): Promise<void> => {
    setRetrying(true);
    try {
      await initialize();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div
      className="
        min-h-dvh flex flex-col items-center justify-center
        bg-[var(--color-bg-app)] px-6
        pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)]
      "
      role="status"
    >
      <div className="w-full max-w-[440px] text-center">
        <div
          className="
            mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full
            bg-[var(--color-bg-secondary)] text-[var(--color-text-tertiary)]
          "
          aria-hidden="true"
        >
          <WifiOff className="h-7 w-7" strokeWidth={1.75} />
        </div>

        <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">Нет связи</h1>
        <p className="mt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">
          Кабинет открылся, но не может связаться с сервером. Проверьте подключение — как только
          связь появится, всё загрузится само.
        </p>

        <button
          type="button"
          onClick={() => void retry()}
          disabled={isRetrying}
          className="
            mt-6 h-12 w-full rounded-[var(--radius-lg)]
            bg-[var(--color-primary-600)] text-white font-medium
            transition-colors hover:bg-[var(--color-primary-700)]
            disabled:opacity-60 disabled:cursor-not-allowed
          "
        >
          {isRetrying ? 'Проверяем…' : 'Повторить'}
        </button>
      </div>
    </div>
  );
}

export default ClientOfflineScreen;
